// components/CameraCapture.jsx - Mobile-First Camera Component
import React, { useState, useRef, useCallback } from 'react';

const CameraCapture = ({ onCapture, onClose, maxPhotos = 5, currentCount = 0 }) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = back camera
  const [isCapturing, setIsCapturing] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      
      // Stop any existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera permissions.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Could not start camera. Try using file upload instead.');
      }
    }
  }, [facingMode, stream]);

  // Initialize camera on mount
  React.useEffect(() => {
    startCamera();
    
    return () => {
      // Cleanup: stop all tracks
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Restart camera when facing mode changes
  React.useEffect(() => {
    if (stream) {
      startCamera();
    }
  }, [facingMode]);

  // Capture photo
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsCapturing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        // Create a File object with timestamp name
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const file = new File([blob], `photo-${timestamp}.jpg`, { type: 'image/jpeg' });
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(blob);
        
        onCapture({
          file,
          previewUrl,
          timestamp: new Date().toISOString(),
          width: canvas.width,
          height: canvas.height
        });
      }
      setIsCapturing(false);
    }, 'image/jpeg', 0.85);
  }, [onCapture]);

  // Switch camera (front/back)
  const switchCamera = useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, []);

  // Handle file input (fallback)
  const handleFileInput = useCallback((e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const previewUrl = URL.createObjectURL(file);
        
        // Get image dimensions
        const img = new Image();
        img.onload = () => {
          onCapture({
            file,
            previewUrl,
            timestamp: new Date().toISOString(),
            width: img.width,
            height: img.height
          });
        };
        img.src = previewUrl;
      }
    });
    
    // Reset input
    e.target.value = '';
  }, [onCapture]);

  const remainingPhotos = maxPhotos - currentCount;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#000',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white'
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '8px'
          }}
        >
          ✕ Close
        </button>
        <span style={{ fontSize: '14px' }}>
          {remainingPhotos} photo{remainingPhotos !== 1 ? 's' : ''} remaining
        </span>
      </div>

      {/* Camera View */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {error ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
            <p style={{ marginBottom: '20px' }}>{error}</p>
            
            {/* File upload fallback */}
            <label style={{
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}>
              Choose from Gallery
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        )}
        
        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        
        {/* Capture flash effect */}
        {isCapturing && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            opacity: 0.8,
            animation: 'flash 0.2s ease-out'
          }} />
        )}
      </div>

      {/* Controls */}
      <div style={{
        padding: '20px',
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center'
      }}>
        {/* Gallery button */}
        <label style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          backgroundColor: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}>
          <span style={{ fontSize: '24px' }}>🖼️</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </label>

        {/* Capture button */}
        <button
          onClick={capturePhoto}
          disabled={!!error || isCapturing || remainingPhotos <= 0}
          style={{
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            border: '4px solid white',
            backgroundColor: remainingPhotos <= 0 ? '#666' : 'white',
            cursor: remainingPhotos <= 0 ? 'not-allowed' : 'pointer',
            opacity: isCapturing ? 0.5 : 1
          }}
        >
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            backgroundColor: remainingPhotos <= 0 ? '#666' : '#ef4444',
            transform: 'scale(0.8)'
          }} />
        </button>

        {/* Switch camera button */}
        <button
          onClick={switchCamera}
          disabled={!!error}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '8px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span style={{ fontSize: '24px' }}>🔄</span>
        </button>
      </div>

      <style>{`
        @keyframes flash {
          from { opacity: 0.8; }
          to { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default CameraCapture;